import { ValidationService } from 'aspnet-client-validation';

function debounce(func, wait, immediate) {
    let timeout;
    return function (...args) {
        const context = this;
        const later = function () {
            timeout = null;
            if (!immediate) func.apply(context, args);
        };
        const callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
    };
}

class EditFormHandler {
    constructor(formElement, endpoint, contentId, editorJsHandler) {
        this.form = formElement;
        this.endpoint = endpoint;
        this.contentId = contentId;
        this.editorJsHandler = editorJsHandler;
        this.debouncedSaveChanges = debounce(this.saveChanges.bind(this), 1500, true);
        this.init();        
    }

    init() {
        this.attachEventListeners();
        this.registerCtrlSSave();

        console.log("Init EditFormHandler");
    }

    attachEventListeners() {

        if (!this.form) {

            console.error('Form not found:', this.form);

            return;
        }

        this.form.addEventListener('submit', (event) => {
            event.preventDefault();
            this.debouncedSaveChanges();

            console.log("Submit");
        });

        // Attach click event to 'Save changes' button
        const saveButton = this.form.querySelector('.save-changes');
        if (saveButton) {
            saveButton.addEventListener('click', (event) => {
                event.preventDefault();
                this.debouncedSaveChanges();
            });
        }

        // Attach event listeners for any other interactive elements, like content tags
        // ...
    }

    registerCtrlSSave() {

        document.addEventListener('keydown', (event) => {
            if (event.ctrlKey && (event.key === 's' || event.keyCode === 83) && this.form.classList.contains('ctrl-s')) {
                event.preventDefault();
                this.debouncedSaveChanges();
            }
        }, false);
    }

    async collectData() {

        let data = { Id: this.contentId };

        this.collectStandardFormFields(data);

        const editorData = await this.collectEditorJSData();
        if (Object.keys(editorData).length > 0) {
            Object.assign(data, editorData);
        }

        this.collectContentTags(data);
        this.collectValueTags(data);


        return data;
    }

    collectStandardFormFields(data) {
        // Query for all relevant fields including select elements and hidden inputs
        const fields = this.form.querySelectorAll('input, textarea, select');

        fields.forEach(field => {
            // Check for field with a name attribute
            if (field.name && field.type !== 'submit') {
                // Handling select elements
                if (field.tagName.toLowerCase() === 'select') {
                    data[field.name] = field.options[field.selectedIndex].value;
                }
                // Handling checkboxes and radio buttons
                else if (field.type === 'checkbox' || field.type === 'radio') {
                    if (field.checked) { // Only if they are checked
                        data[field.name] = field.value;
                    }
                }
                // Handling hidden fields as well as other types
                else {
                    data[field.name] = field.value;
                }
                //console.log(`Added ${field.tagName} field: ${field.name} = ${field.value}`);
            }
        });
    }

    async collectEditorJSData() {

        let editorDataObj = {};

        if (!this.editorJsHandler) {
            return editorDataObj;
        }

        const editorInstances = this.editorJsHandler.getEditorInstances();

        if (Object.keys(editorInstances).length === 0) {
            return editorDataObj;
        }

        for (const [holderId, editor] of Object.entries(editorInstances)) {

            let editorData = await editor.save();

            if (editorData.blocks.length > 0) {
                const adjustedHolderId = holderId.replace('_editorjs', '');
                editorDataObj[adjustedHolderId] = JSON.stringify(editorData);
            }
        }

        return editorDataObj;
    }

    collectContentTags(data) {
        const contentTags = this.form.querySelectorAll('.content-tag.on');

        if (contentTags.length <= 0) {
            return;
        }

        const activeTags = Array.from(contentTags).map(tag => tag.getAttribute('data-id'));
        data['content-tags'] = activeTags;
        console.log('Added content tags:', activeTags);
    }

    collectValueTags(data) {
        const contentTags = this.form.querySelectorAll('.value-tag.on');

        if (contentTags.length <= 0) {
            return;
        }

        const activeTags = Array.from(contentTags).map(tag => tag.getAttribute('data-id'));
        data['value-tags'] = activeTags;
        console.log('Added value tags:', activeTags);
    }

    async saveChanges() {

        if (this.form.classList.contains('validate')) {

            let validationService = new ValidationService();
            validationService.bootstrap({ root: this.form });

            const isValid = await validationService.isValid(this.form, true);

            if (!isValid) {

                console.error('Validation failed');
                return;
            }
        }

        const data = await this.collectData();

        const token = this.form.querySelector('input[name="__ReqVfTok"]').value;

        try {

            const response = await fetch(this.endpoint, {

                method: 'POST',

                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-vToken': token
                },

                body: JSON.stringify(data),
            });

            if (response.ok) {
                try {
                    const result = await response.json();

                    if (result.success) {
                        document.dispatchEvent(new CustomEvent('formSaved', { detail: result }));
                    }
                    else {
                        throw new Error(result.message || 'An unknown error occurred');
                    }
                }
                catch (jsonError) {
                    throw new Error('Server response was not valid JSON.');
                }
            }
            else if (response.status === 400) {
                const errorResult = await response.json();
                this.displayValidationErrors(errorResult.errors);
            } else {
                const errorResult = await response.json();
                throw new Error(`Server responded with status ${response.status}: ${errorResult.message}`);
            }

        } catch (error) {
            console.error('Save failed:', error);

            // Dispatch an event for a failed save if necessary
            document.dispatchEvent(new CustomEvent('formSaveFailed', { detail: error }));
        }
    }

    // Additional methods for validation, etc.
    // ...

    displayValidationErrors(errors) {
        // Clear previous validation errors
        const validationSpans = this.form.querySelectorAll('span[data-valmsg-for]');
        validationSpans.forEach(span => {
            span.innerText = '';
        });

        // Display new validation errors
        for (const key in errors) {
            if (errors.hasOwnProperty(key)) {
                const span = this.form.querySelector(`span[data-valmsg-for="${key}"]`);
                if (span) {
                    span.innerText = errors[key].join(', ');
                }
            }
        }
    }
}

export { EditFormHandler };